/**
 * WalletConnect often throws "duplicate call detected" while the first
 * eth_sendTransaction is still open. Prefer the writeContract hash; fall back
 * to Transfer log scans via Infura (VITE_BSC_RPC_URL) or wagmi publicClient.
 */
import {
  createPublicClient,
  erc20Abi,
  http,
  parseAbiItem,
  type Address,
  type Hash,
  type PublicClient,
} from 'viem';
import { bsc } from 'viem/chains';

const TRANSFER_EVENT = parseAbiItem(
  'event Transfer(address indexed from, address indexed to, uint256 value)',
);

const LOG_CHUNK = 50n;
const MAX_LOOKBACK = 2000n;

export function isUserRejectMessage(msg: string): boolean {
  return /user rejected|denied|rejected the request|用户拒绝/i.test(msg);
}

export function isWalletInFlightMessage(msg: string): boolean {
  return /duplicate call|already pending|request already pending|transaction already/i.test(
    msg,
  );
}

function errMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e ?? 'transfer failed');
}

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

/** Prefer Infura / custom RPC baked at build time; never log the URL with secrets. */
export function createBscScanClient(
  fallback?: PublicClient,
): PublicClient | undefined {
  const url = (import.meta.env.VITE_BSC_RPC_URL as string | undefined)?.trim();
  if (url) {
    return createPublicClient({
      chain: bsc,
      transport: http(url),
    });
  }
  return fallback;
}

export async function findMatchingErc20Transfer(opts: {
  publicClient: PublicClient;
  token: Address;
  from: Address;
  to: Address;
  minRaw: bigint;
  fromBlock: bigint;
}): Promise<Hash | null> {
  const latest = await opts.publicClient.getBlockNumber();
  let cursor =
    opts.fromBlock > 0n
      ? opts.fromBlock
      : latest > MAX_LOOKBACK
        ? latest - MAX_LOOKBACK
        : 0n;
  if (latest > cursor + MAX_LOOKBACK) {
    cursor = latest - MAX_LOOKBACK;
  }

  let best: Hash | null = null;
  while (cursor <= latest) {
    const toBlock = cursor + LOG_CHUNK > latest ? latest : cursor + LOG_CHUNK;
    try {
      const logs = await opts.publicClient.getLogs({
        address: opts.token,
        event: TRANSFER_EVENT,
        args: { from: opts.from, to: opts.to },
        fromBlock: cursor,
        toBlock,
      });
      for (const log of logs) {
        if (log.args.value != null && log.args.value >= opts.minRaw && log.transactionHash) {
          best = log.transactionHash;
        }
      }
    } catch {
      /* RPC limit / transient — skip chunk */
    }
    cursor = toBlock + 1n;
  }
  return best;
}

/**
 * Await writeContract hash; on WalletConnect "duplicate call", keep polling
 * for a matching Transfer until timeout.
 */
export async function waitForUsdtTransferOrHash(opts: {
  writePromise: Promise<Hash>;
  publicClient: PublicClient | undefined;
  token: Address;
  from: Address;
  to: Address;
  minRaw: bigint;
  startBlock: bigint;
  timeoutMs?: number;
  timeoutMessage?: string;
}): Promise<Hash> {
  const timeoutMs = opts.timeoutMs ?? 180_000;
  const started = Date.now();
  const scanClient = createBscScanClient(opts.publicClient);
  type WriteOutcome = { ok: true; hash: Hash } | { ok: false; err: unknown };
  const writeBox: { current: WriteOutcome | null } = { current: null };
  void opts.writePromise.then(
    (hash) => {
      writeBox.current = { ok: true, hash };
    },
    (err: unknown) => {
      writeBox.current = { ok: false, err };
    },
  );

  while (Date.now() - started < timeoutMs) {
    const settled = writeBox.current;
    if (settled?.ok === true) return settled.hash;

    if (scanClient) {
      try {
        const found = await findMatchingErc20Transfer({
          publicClient: scanClient,
          token: opts.token,
          from: opts.from,
          to: opts.to,
          minRaw: opts.minRaw,
          fromBlock: opts.startBlock,
        });
        if (found) return found;
      } catch {
        /* keep waiting */
      }
    }

    if (settled?.ok === false) {
      const msg = errMessage(settled.err);
      if (isUserRejectMessage(msg)) {
        throw new Error('已取消钱包签名');
      }
      if (!isWalletInFlightMessage(msg)) {
        throw settled.err instanceof Error ? settled.err : new Error(msg);
      }
    }

    await sleep(2000);
  }
  throw new Error(
    opts.timeoutMessage ??
      '等待钱包确认超时。若钱包已发送成功，请粘贴交易哈希提交入账',
  );
}

/** Shared writeContract dedupe (WalletConnect duplicate call). */
export const inflightUsdtWrites = new Map<string, Promise<Hash>>();

export { erc20Abi };
